import { clear } from 'idb-keyval';
import { IAirwebTicketLegacyUserInfo } from '@airweb/ticket-legacy-client';

import pkg from '@/package.json';
import { useCart } from './cart';
import { useRenew } from './renew';
import { DynamicForm, useSchool } from './school';
import { useNetwork } from './network';
import { useDuplicate } from './duplicate';
import { TODO } from '@/types/todo';
import type { CookieOptions } from '#app';

function temporarlyFixString(string?: string) {
  if (!string) return '';

  return string.replaceAll("'", '’');
}

export const sessionCookieName = 'AIRWEB_ESHOP_SESSION';
export const sessionCookieConfig: CookieOptions = { sameSite: 'strict', path: '/' };

export const useAuth = defineStore('auth', {
  state: () => ({
    user: null as IAirwebTicketLegacyUserInfo | null,
    profiles: [] as IAirwebTicketLegacyUserInfo[],
  }),
  getters: {
    isLoggedIn(): boolean {
      return !!this.user;
    },
    isAccountVerified(): boolean {
      return Boolean(this.user?.verifiedAt);
    },
    fullname(): string {
      if (!this.user) return '';
      return `${this.user.userName} ${this.user.userLastname}`;
    },
  },
  actions: {
    async login(body: { email: string; password: string }) {
      const nuxtApp = useNuxtApp();
      const network = useNetwork();

      // Get the current browser user agent
      // TODO: You can probably get the user agent fron `useHeaders().host`
      // when on the `process.server`
      const originAgent = process.client
        ? [...navigator.userAgent.split(' '), `AirwebTicketSchoolEshop/${pkg.version}`].join(' ')
        : '';

      const fireAuth = await nuxtApp.$firebase.login(body);

      const ticketAuth = await nuxtApp.$ticket.account.login(fireAuth.idToken, {
        originType: 'SCHOOL',
        originAgent,
        forceProfiles: false,
        authAsGuest: false,
        networkId: network.config.id,
      });

      const airwebSession = useCookie(sessionCookieName, sessionCookieConfig);
      airwebSession.value = ticketAuth.userToken;

      this.user = await nuxtApp.$ticket.account.getUser(ticketAuth.userToken, undefined, {
        networkId: network.config.id,
      });
      this.getProfiles();

      return this.user;
    },
    async getUser() {
      if (!this.user) return;

      const nuxtApp = useNuxtApp();
      const network = useNetwork();

      this.user = await nuxtApp.$ticket.account.getUser(this.user?.userToken, undefined, {
        networkId: network.config.id,
      });

      return this.user;
    },
    async getProfiles() {
      const nuxtApp = useNuxtApp();
      const network = useNetwork();

      const profiles = await nuxtApp.$ticket.account.getProfiles(this.user?.userToken!, {
        networkId: network.config.id,
      });

      const waiting: Promise<IAirwebTicketLegacyUserInfo>[] = [];

      for (const profile of profiles) {
        if (profile.isParent) continue;
        const promise = nuxtApp.$ticket.account.getUser(this.user?.userToken!, profile.userId, {
          networkId: network.config.id,
        });
        waiting.push(promise);
      }

      this.profiles = await Promise.all(waiting);

      return this.profiles;
    },
    async signUp(body: { email: string; password: string; firstname: string; lastname: string }) {
      const cart = useCart();
      const nuxtApp = useNuxtApp();
      const network = useNetwork();

      // Get the current browser user agent
      // TODO: If we are on the server, we can retrieve this config using `useHeaders` and grabbing the `host` header
      const originAgent = process.client
        ? [...navigator.userAgent.split(' '), `AirwebTicketSchoolEshop/${pkg.version}`].join(' ')
        : '';

      /**
       * [THIS COMMENT IS AUTO GENERATED BY COPILOT X]
       *
       * This code is a function that signs up a user using Firebase authentication.
       * It takes a body object as an argument, which contains the user's email and password.
       * The function first calls the signUp method on the $firebase object, which is a Firebase authentication service provided by the Nuxt.js framework.
       * If the sign-up is successful, the function returns the fireAuth object that is returned by the signUp method.
       *
       * However, if the sign-up fails, the function catches the error and checks if the error message is "EMAIL_EXISTS".
       * This error message indicates that the user's email address is already registered with Firebase.
       * In this case, the function attempts to log in the user using the login method on the $firebase object.
       * If the login is successful, the function returns the fireAuth object that is returned by the login method.
       *
       * If the login also fails, the function re-throws the original error, which will be caught by the calling function.
       * This ensures that any errors that occur during the sign-up or login process are properly handled by the calling code.
       *
       * Overall, this code provides a simple and robust way to sign up and log in users using Firebase authentication in a Nuxt.js application.
       * By handling errors and checking for existing email addresses, the function ensures that users are properly authenticated and that errors are handled gracefully.
       */
      const fireAuth = await nuxtApp.$firebase.signUp(body);

      const ticketAuth = await nuxtApp.$ticket.account.login(fireAuth.idToken, {
        originType: 'SCHOOL',
        originAgent,
        forceProfiles: false,
        authAsGuest: false,
        networkId: network.config.id,
      });

      const airwebSession = useCookie(sessionCookieName, sessionCookieConfig);
      airwebSession.value = ticketAuth.userToken;

      const accountUpdate = await nuxtApp.$ticket.account.update(
        ticketAuth.userToken,
        ticketAuth.userId,
        {
          userName: cart.account.userName,
          userLastname: cart.account.userLastname,
          userMail: cart.account.userMail,
          userPhoneNumber: cart.account.userPhoneNumber,
          userBirthday: cart.account.userBirthday,
          userNetworkIds: [+network.config.id],
          address: {
            addressStreetNumber: '',
            addressRoute: cart.account.address.addressRoute,
            addressPostalCode: cart.account.address.addressPostalCode,
            addressCity: cart.account.address.addressCity,
            addressState: cart.account.address.addressState,
          },
        },
        {
          networkId: network.config.id,
        },
      );

      this.user = await nuxtApp.$ticket.account.getUser(ticketAuth.userToken, undefined, {
        networkId: network.config.id,
      });

      return accountUpdate;
    },
    async resetPassword(body: { email: string; networkId: string }) {
      const nuxtApp = useNuxtApp();
      await nuxtApp.$ticket.account.resetPassword(body.email, body.networkId);
    },
    async updatePassword(body: {
      currentPassword: string;
      newPassword: string;
      confirmPassword: string;
    }) {
      const nuxtApp = useNuxtApp();

      if (!this.user) return;

      const fireAuth = await nuxtApp.$firebase.login({
        email: this.user.userMail,
        password: body.currentPassword,
      });

      const updatePasswordResult = await nuxtApp.$firebase.updatePassword({
        idToken: fireAuth.idToken,
        password: body.newPassword,
      });

      return updatePasswordResult;
    },
    async createProfile(data: {
      picture?: File | null;
      answers?: DynamicForm;
      creationData?: Partial<IAirwebTicketLegacyUserInfo>;
    }): Promise<IAirwebTicketLegacyUserInfo | undefined> {
      const nuxtApp = useNuxtApp();
      const network = useNetwork();
      if (!data.creationData) return;

      const profile = await nuxtApp.$ticket.account.createProfile(
        this.user?.userToken!,
        {
          userName: temporarlyFixString(data.creationData.userName),
          userLastname: temporarlyFixString(data.creationData.userLastname),
          userPhoneNumber: temporarlyFixString(data.creationData.userPhoneNumber),
          userBirthday: data.creationData.userBirthday,
        },
        network.config.id,
      );

      if (data.picture) {
        try {
          await this.uploadAvatar(profile.userId, data.picture);
        } catch (error$: TODO) {
          if (error$?.data) {
            // The error comes from ofetch (internal SDK, firebase)
            useNuxtApp().$sentryCaptureException(error$.data);
            console.log(error$.data);
          } else if (error$?.response?.data) {
            // The error comes from the airweb SDK
            useNuxtApp().$sentryCaptureException(error$.response.data);
            console.log(error$.response.data);
          } else {
            useNuxtApp().$sentryCaptureException(error$);
            console.log(error$.message);
          }
        }
      }

      // I send this request to have to have the good typescript type, `createProfile` send `IAirwebTicketLegacyUserProfile` an I want `IAirwebTicketLegacyUserInfo`
      return nuxtApp.$ticket.account.getUser(this.user?.userToken!, profile.userId, {
        networkId: network.config.id,
      });
    },
    sendVerificationEmail() {
      const nuxtApp = useNuxtApp();
      const network = useNetwork();

      return nuxtApp.$ticket.account.sendVerificationEmail(this.user?.userToken!, {
        networkId: network.config.id,
      });
    },
    async logout() {
      const cart = useCart();
      const renew = useRenew();
      const school = useSchool();
      const nuxtApp = useNuxtApp();
      const duplicate = useDuplicate();
      const airwebSession = useCookie(sessionCookieName, sessionCookieConfig);

      const userToken = airwebSession.value;
      if (!this.isLoggedIn || !userToken || !this.user) return;

      try {
        await nuxtApp.$ticket.account.logout(userToken, this.user.userId.toString());

        clear();
        cart.$reset();
        renew.$reset();
        school.$reset();
        duplicate.$reset();
        this.$reset();
      } catch (error) {
        console.error(error);
      } finally {
        airwebSession.value = null;
      }
    },
    uploadAvatar(userId: string, file: File) {
      const nuxtApp = useNuxtApp();
      console.log('upload avatar', this.user?.userToken!, userId, file);

      return nuxtApp.$ticket.media.uploadMedia(this.user?.userToken!, userId, file);
    },
    updateUserToken(userToken: string) {
      if (this.user) {
        this.user.userToken = userToken;

        const airwebSession = useCookie(sessionCookieName, { sameSite: 'strict', path: '/' });
        airwebSession.value = userToken;
      }
    },
    async reactivateAccount() {
      if (this.user) {
        const nuxtApp = useNuxtApp();

        await nuxtApp.$ticket.http.post('/Account/reactivate', { userToken: this.user.userToken });
        this.user.userAnonymizationRequestedAt = null;
      }
    },
  },
});
